// The full set of UI message keys. Every locale must provide all of them (the `Messages` type
// enforces completeness at compile time), so a missing translation is a build error, not a blank.

export type MessageKey =
  | 'app.loading'
  | 'upload.title'
  | 'upload.parsing'
  | 'upload.hint'
  | 'upload.privacy'
  | 'player.chapters'
  | 'player.search'
  | 'player.prevChapter'
  | 'player.nextChapter'
  | 'player.back'
  | 'player.forward'
  | 'player.play'
  | 'player.pause'
  | 'chapters.title'
  | 'chapters.openOther'
  | 'search.placeholder'
  | 'search.none'
  | 'voice.label'
  | 'voice.none'
  | 'voice.engine'
  | 'voice.provider.system'
  | 'voice.provider.kokoro'
  | 'voice.loadingModel'
  | 'voice.modelError'
  | 'speed.label'
  | 'theme.toggle'
  | 'common.close'
  | 'toast.nowReading'
  | 'toast.saveFailed'
  | 'toast.playbackError'
  | 'toast.loadFailed';

export type Messages = Record<MessageKey, string>;

// English is the source of truth and ships in the main bundle (the common case downloads nothing
// extra). It is also the fallback when a locale is unknown or its chunk fails to load.
export const en: Messages = {
  'app.loading': 'Steeping…',
  'upload.title': 'Bring a book',
  'upload.parsing': 'Reading your book…',
  'upload.hint': 'Drop a book here, or tap to choose',
  'upload.privacy': 'Everything stays on your device — no account, no upload, no server.',
  'player.chapters': 'Chapters',
  'player.search': 'Search',
  'player.prevChapter': 'Previous chapter',
  'player.nextChapter': 'Next chapter',
  'player.back': 'Back 15 seconds',
  'player.forward': 'Forward 15 seconds',
  'player.play': 'Play',
  'player.pause': 'Pause',
  'chapters.title': 'Chapters',
  'chapters.openOther': 'Open a different book',
  'search.placeholder': 'Find a word or phrase, then jump to it…',
  'search.none': 'No matches',
  'voice.label': 'Reading voice',
  'voice.none': 'No voices',
  'voice.engine': 'Voice engine',
  'voice.provider.system': 'Device voices',
  'voice.provider.kokoro': 'Natural (Kokoro)',
  'voice.loadingModel': 'Downloading voice model… {percent}%',
  'voice.modelError': 'Voice model failed to load. Check your connection and try again.',
  'speed.label': 'Playback speed',
  'theme.toggle': 'Toggle theme',
  'common.close': 'Close',
  'toast.nowReading': 'Now reading: {title}',
  'toast.saveFailed': 'Could not save your place.',
  'toast.playbackError': 'Playback stopped unexpectedly.',
  'toast.loadFailed': "Couldn't open this book. It may be unsupported, empty, or protected.",
};

// Locales we ship beyond English. Each is a lazily-imported chunk (see I18n.load). Add a language by
// dropping a `<code>.ts` beside this file and listing the code here.
export const LAZY_LOCALES = ['fr', 'es', 'de', 'zh'] as const;
export type LazyLocale = (typeof LAZY_LOCALES)[number];
export type Locale = 'en' | LazyLocale;

/** The base language of a tag, normalized: "fr-FR" / "fr_FR" / "FR" all become "fr". */
export function baseLang(tag: string | undefined): string {
  return (tag ?? '').toLowerCase().split(/[-_]/)[0];
}

/** Map a BCP-47 browser language (e.g. "fr-FR", "zh-Hans-CN") to a supported locale, else English. */
export function resolveLocale(language: string | undefined): Locale {
  const base = baseLang(language) || 'en';
  if (base === 'en') return 'en';
  return (LAZY_LOCALES as readonly string[]).includes(base) ? (base as Locale) : 'en';
}

/** Substitute `{name}` placeholders. Missing vars are left as-is. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}
